import React from 'react';

import { faCaretDown, faCaretUp,faCaretLeft,faCaretRight, faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Modal } from 'Services';
import { asset } from "Services/BaseHelpers";

import { gameGridConfig } from './GameGrid';
import GameModal from './GameModal';

/*
- We generate the grid for this game from the "gameGridConfig". That config contains each of the positions and defines walls that exist around each position.
- When the user clicks to start the game we set a method "handleMoves" to be called on an interval.
- That "handleMoves" method will move each of the "movers" to their next position.
- Then at the end of "handleMoves" we complete the moves by updating the state variables "points", "eaten" and "secondsRemaining".
- A point is awarded every time you hit a position that is not yet in the eaten array.
- Some positions contain different types (see the gameGridConfig) such an "antivirus" that give multiple points.
- Additionally if the "me" mover hits any of the other movers then we pause the game and display a popup with some information about that mover.
*/

export default class Game extends React.Component {
    /**
     * @var moveInterval (milliseconds)
     * @type {integer}
     */
    moveInterval = 500;

    /**
     * @var moveIntervalTimeout
     */
    moveIntervalTimeout = null;

    /**
     * @var antivirusPoints
     * @type {integer}
     */
    antivirusPoints = 5;

    /**
     * @var topBlocks
     * @type {array}
     */
    topBlocks = [
        {
            image: 'norton.png',
            text: <div>
                <span className="text-app-yellow">
                    Collect{" "}
                </span>

                The Norton 'Protection Shields'
            </div>,
        },
        {
            image: 'virus.png',
            text: <div>
                <span className="text-app-yellow">
                    Avoid{" "}
                </span>

                The 'Vicious Viruses'
            </div>,
        },
        {
            image: 'hacker-man.png',
            text: <div>
                <span className="text-app-yellow">
                    Avoid{" "}
                </span>

                The 'Cybercrook'
            </div>,
        }
    ]

    /**
     * Here we define all of the movers and their start positions and directions.
     * Additionally we define the points counter to start at 0 and the eaten array to be empty by default.
     * We also set the secondsRemaining value for the timer countdown.
     * 
     * @var state
     */
    state = {
        movers: [
            {
                type: 'me',
                image: 'ee_logo.png',
                x: 6,
                y: 9,
                direction: 'left',
                previousDirection: null
            },
            {
                type: 'virus',
                image: 'virus.png',
                x: 1,
                y: 3,
                direction: 'up',
            },
            {
                type: 'virus',
                image: 'virus.png',
                x: 1,
                y: 4,
                direction: 'down',
            },
            {
                type: 'virus',
                image: 'virus.png',
                x: 6,
                y: 2,
                direction: 'left',
            },
            {
                type: 'hacker',
                image: 'hacker-man.png',
                x: 5,
                y: 1,
                direction: 'left',
            }
        ],
        points: 0,
        eaten: [],
        secondsRemaining: 90,
        started: false,
        scale: 1
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        // Set up the keyboard controls.
        document.addEventListener("keydown", this.handleKeyboardControls);

        // Set up the initial scale.
        this.setScale();

        // Set the scale to update if the window size updates.
        window.addEventListener('resize', this.setScale);
    }

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        // Clear the move interval.
        if (this.moveIntervalTimeout) {
            clearInterval(this.moveIntervalTimeout);
        }

        // Remove the keyboard controls listener.
        document.removeEventListener("keydown", this.handleKeyboardControls);

        // Remove the resize window listener
        window.removeEventListener('resize', this.setScale);
    }

    /**
     * @method setScale
     */
    setScale = () => {
        const {scale} = this.state;

        let newScale = 1;
        let innerWidth = window.innerWidth;

        // Scale is 1 above 1400, otherwise we scale.
        if (innerWidth < 1400) {
            newScale = innerWidth / 1400;
        }

        if (newScale !== scale) {
            this.setState({
                scale: newScale
            });
        }
    }

    /**
     * @method handleStart
     */
    handleStart = () => {
        const {started} = this.state;

        if (!this.moveIntervalTimeout) {
            // Set up the move interval.
            this.moveIntervalTimeout = setInterval(this.handleMoves, this.moveInterval);

            // Set started state to true.
            this.setState({
                started: true
            });
        }
    }

    /**
     * @method handlePause
     */
    handlePause = () => {
        // Clear the move interval.
        clearInterval(this.moveIntervalTimeout);
        this.moveIntervalTimeout = null;
    }

    /**
     * @method handleEnd
     * @param {boolean} gameCompleted
     */
    handleEnd = (gameCompleted) => {
        const {points} = this.state;
        const {setParentState} = this.props;

        // Clear the move interval.
        clearInterval(this.moveIntervalTimeout);
        this.moveIntervalTimeout = null;

        // Pass the score back to the parent and move to the end stage.
        setParentState({
            stage: 'end',
            score: points,
            gameCompleted
        });
    }

    /**
     * See the notes at the top of this script for how this works.
     * 
     * @method handleMoves
     */
    handleMoves = () => {
        let {movers} = this.state;

        let newMovers = [];

        // Move each of the movers.
        movers.forEach((mover, key) => {
            newMovers[key] = this.handleMove(mover);
        });

        // Complete the moves.
        this.completeMoves(newMovers, movers);
    }

    /**
     * @method handleMove
     * @param {object} mover
     * @return {object}
     */
    handleMove = (mover) => {
        let newMover = mover;
        
        // Attempt to move this mover and if it hits a wall then we keep changing
        // direction until we can make a move.
        while (
            mover.x === newMover.x && mover.y === newMover.y
        ) {
            // Set next move.
            newMover = this.setNextMove(newMover);

            // We have not moved (i.e. we have hit a wall) so change direction.
            if (mover.x === newMover.x && mover.y === newMover.y) {
                newMover = this.setNextDirection(newMover);
            }
        };

        return newMover;
    }

    /**
     * @method setNextMove
     * @param {object} mover
     * @return {object}
     */
    setNextMove = (mover) => {
        let {x, y, direction} = mover;

        // Get the current grid position object.
        let currentPosition = gameGridConfig[y - 1][x - 1];

        // If the current position's border and the next position's border are
        // both false for this move then there is no wall in our way so we make the move.
        if (direction === 'up' && !currentPosition.top && !gameGridConfig[y - 2][x - 1].bottom) {
            y = y - 1;
        } else if (direction === 'down' && !currentPosition.bottom && !gameGridConfig[y][x - 1].top) {
            y = y + 1;
        } else if (direction === 'left' && !currentPosition.left && !gameGridConfig[y - 1][x - 2].right) {
            x = x - 1;
        } else if (direction === 'right' && !currentPosition.right && !gameGridConfig[y - 1][x].left) {
            x = x + 1;
        }

        return {
            ...mover,
            x,
            y
        }
    }

    /**
     * @method setNextDirection
     * @param {object} mover
     * @return {object}
     */
    setNextDirection = (mover) => {
        let direction = mover.direction;

        // For the "me" mover we keep track of the previous direction 
        // so that if the user uses the controls to move in to a wall 
        // then we just ignore it and revert back to the previous direction.
        if (mover.previousDirection) {
            return {
                ...mover,
                previousDirection: null,
                direction: mover.previousDirection
            }
        }

        // Loop through the directions.
        switch(mover.direction) {
            case 'left':
                direction = 'up';
                break;
            case 'up':
                direction = 'right';
                break;
            case 'right':
                direction = 'down';
                break;
            case 'down':
                direction = 'left';
                break;
            default:
                break;
        }

        return {
            ...mover,
            direction
        }
    }

    /**
     * @method completeMoves
     * @param {array} newMovers
     * @param {array} existingMovers
     */
    completeMoves = (newMovers, existingMovers) => {
        let {points, eaten, secondsRemaining} = this.state;

        // Get current x and y position of the "me" mover.
        let {x, y} = newMovers.find(m => m.type === 'me');

        // Update secondsRemaining.
        secondsRemaining--;

        // Get the new me grid position object.
        let newPosition = gameGridConfig[y - 1][x - 1];

        // If at the end then stop.
        if (newPosition.type === 'end') {
            this.handleEnd(true);
        }

        // If timer has run out then stop.
        if (secondsRemaining === 0) {
            this.handleEnd(false);
        }

        // If position not already eaten.
        if (!eaten.find(e => e.x === x && e.y === y)) {
            // Add points.
            if (newPosition.type === 'point') {
                points++;
            } else if (newPosition.type === 'antivirus') {
                points += this.antivirusPoints;

                this.handlePopup('antivirus');
            }

            // Add this new position to the eaten list.
            eaten.push({
                x,
                y
            });
        }

        // Update the state.
        this.setState({
            movers: newMovers,
            points,
            eaten,
            secondsRemaining
        }, () => {
            // See notes above the method below.
            this.checkForPopUp(newMovers, existingMovers);
        });
    }
    
    /**
     * If the "me" mover hits any of the other movers then we 
     * pause the game and display a popup with some information 
     * about that mover.
     *
     * @method checkPopUp
     * @param {array} newMovers
     * @param {array} existingMovers
     */
    checkForPopUp = (newMovers, existingMovers) => {
        // Get new "me" mover.
        let me = newMovers.find(m => m.type === 'me');

        // Go through each of the movers where type !== 'me'
        // and if the mover is at the same position as the me
        // object (i.e. they have hit each other) then display a 
        // popup modal.
        
        // Go through current positions first.
        for (var i=0; i<existingMovers.length; i++) {
            this.checkForPopUp2(me, existingMovers[i], i);
        }

        // Then go through the new positions.
        for (var i=0; i<newMovers.length; i++) {
            this.checkForPopUp2(me, newMovers[i], i);
        }
    }

    /**
     * @method checkForPopUp2
     * @param {object} me
     * @param {object} mover
     * @param {integer} key
     */
    checkForPopUp2 = (me, mover, key) => {
        const {movers} = this.state;

        // If hit each other.
        if (mover.type !== 'me' && mover.x === me.x && mover.y === me.y) {
            // Remove this mover from the screen.
            this.setState({
                movers: movers.filter((mover, i) => i !== key)
            });

            // Display the popup.
            this.handlePopup(mover.type);
        }
    }

    /**
     * @method handlePopup
     * @param {string} type
     */
    handlePopup = (type) => {
        // Pause.
        this.handlePause();

        // Display the popup modal.
        Modal.open({
            component: GameModal,
            props: {
                type,
                handleStartCallback: this.handleStart,
                antivirusPoints: this.antivirusPoints
            }
        });
    }

    /**
     * If the arrow keys on the keyboard are used
     * then change the direction of the "me" mover accordingly.
     *
     * @method handleKeyboardControls
     * @param {event} event
     */
    handleKeyboardControls = (event) => {
        let newDirection = null;

        switch(event.keyCode) {
            case 37: // arrow left
                newDirection = 'left';
                event.preventDefault();
                break;
            case 38: // arrow up
                newDirection = 'up';
                event.preventDefault();
                break;
            case 39: // arrow right
                newDirection = 'right';
                event.preventDefault();
                break;
            case 40: // arrow down
                newDirection = 'down';
                event.preventDefault();
                break;
            default:
                break;
        }

        if (newDirection) {
            this.handleMeDirectionChange(newDirection);
        }
    }

    /**
     * @method handleMeDirectionChange
     * @param {string} direction
     */
    handleMeDirectionChange = (direction) => {
        const {movers} = this.state;

        this.setState({
            movers: movers.map((mover) => {
                if (mover.type === 'me') {
                    mover.previousDirection = mover.direction;
                    mover.direction = direction;
                }

                return mover;
            })
        });
    }

    /**
     * @method render
     * @return {*}
     */
    render () {
        const {started} = this.state;

        return (
            <div className="h-full">
                {this.renderSideControls()}

                <div className="flex flex-col justify-between gap-4 xl:gap-6 h-full">
                    {this.renderTopCreative()}
                    {this.renderGrid()}

                    <div>
                        {!started &&
                            <p
                                className="font-thin text-white text-center mb-6 cursor-pointer retro-gaming uppercase text-sm md:text-base"
                                onClick={this.handleStart}
                            >
                                Click here to start!
                            </p>
                        }
                    </div>
                </div>
            </div>
        );
    }

    /**
     * @method renderTopCreative
     * @return {*}
     */
    renderTopCreative() {
        return (
            <div className="flex flex-row justify-center xl:justify-between items-center gap-6 text-xs uppercase rubrik-light text-white">
                <div className={`
                    uppercase retro-gaming text-xl md:text-2xl xl:text-5xl 2xl:text-7xl font-extrabold 
                    text-transparent text-center bg-clip-text bg-gradient-to-t 
                    from-app-yellow to-app-red
                `}>
                    Play
                </div>

                <div className={`
                    hidden xl:flex flex-col md:flex-row justify-center items-center gap-2 2xl:gap-4 
                    text-left uppercase rubrik-light text-white font-thin
                    border-2 border-dashed border-app-yellow rounded-xl p-2 2xl:p-4
                `}>
                    {this.topBlocks.map((block, i) => (
                        <div
                            className="flex flex-row justify-center items-center gap-2 2xl:gap-4 text-xs"
                            key={i}
                        >
                            <img
                                src={asset(`images/pacman/${block.image}`)}
                                className="w-6"
                            />

                            {block.text}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    /**
     * @method renderSideControls
     * @return {*}
     */
    renderSideControls() {
        const {points, secondsRemaining} = this.state;

        return (
            <div className="absolute top-0 bottom-0 my-auto -right-36 flex flex-col justify-center items-center gap-4 md:gap-5">
                <div className={`
                    font-bold text-white rounded-full bg-app-turquoise
                    flex flex-col justify-center items-center text-center font-mono
                    h-16 w-16 mdh-24 md:w-24 lg:h-28 lg:w-28 2xl:h-[8.5rem] 2xl:w-[8.5rem] p-2 lg:p-4
                `}>
                    <div className="uppercase nobblee-bold font-bolder md:text-2xl lg:text-4xl 2xl:text-6xl">
                        {secondsRemaining}
                    </div>

                    <div className="hidden md:block uppercase nobblee-bold text-bold text-xs lg:text-md">
                        Seconds<br/>Left
                    </div>

                    <div className="block md:hidden uppercase nobblee-bold text-bold text-[0.4rem]">
                        Seconds
                    </div>
                </div>

                <div className={`
                    font-bold text-turquoise rounded-full bg-app-yellow
                    flex flex-col justify-center items-center text-center font-mono
                    h-16 w-16 mdh-24 md:w-24 lg:h-28 lg:w-28 2xl:h-[8.5rem] 2xl:w-[8.5rem] p-2 lg:p-4
                `}>
                    <div className="uppercase nobblee-bold font-bolder md:text-2xl lg:text-4xl 2xl:text-6xl">
                        {points}
                    </div>

                    <div className="uppercase nobblee-bold text-bold text-[0.4rem] md:text-xs lg:text-md">
                        Points
                    </div>
                </div>

                <div className="grid grid-cols-3 w-[7.5rem] h-[7.5rem] 2xl:w-36 2xl:h-36">
                    <div/>
                    {this.renderSideControlArrow('up', faCaretUp)}
                    <div/>

                    {this.renderSideControlArrow('left', faCaretLeft)}
                    {this.renderSideControlArrow('center', faCircle)}
                    {this.renderSideControlArrow('right', faCaretRight)}

                    <div/>
                    {this.renderSideControlArrow('down', faCaretDown)}
                    <div/>
                </div>
            </div>
        );
    }

    /**
     * @method renderSideControlArrow
     * @param {string} direction
     * @param {FaIcon} icon
     * @return {*}
     */
    renderSideControlArrow(direction, icon) {
        const {movers, started} = this.state;

        return (
            <div
                className="w-10 h-10 2xl:w-12 2xl:h-12 bg-white"
                onClick={() => direction !== 'center' ? 
                    this.handleMeDirectionChange(direction) : 
                    null}
            >
                <FontAwesomeIcon
                    icon={icon}
                    size="2x"
                    className={`
                        mx-auto w-full h-full text-app-turquoise
                        ${direction !== 'center' && started ? 
                            'hover:text-app-yellow cursor-pointer' : 
                            ''}
                    `}
                />
            </div>
        );
    }

    /**
     * @method renderGrid
     * @return {*}
     */
    renderGrid() {
        return (
            <div className="flex flex-col justify-center items-center text-center w-max mx-auto">
                {gameGridConfig.map((row, i) => (
                    <div
                        className="flex flex-row justify-center items-center text-center"
                        key={i}
                    >
                        {row.map((cell, j) => (
                            <div
                                className="flex justify-center items-center text-center"
                                key={j}
                            >
                                {this.renderCell(cell, (j + 1), (i + 1))}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    /**
     * @method renderCell
     * @param {object} cell
     * @param {integer} x
     * @param {integer} y
     * @return {*}
     */
    renderCell(cell, x, y) {
        return (
            <div style={this.getStyleForCell(cell)}>
                {this.renderCellContents(cell, x, y)}
            </div>
        );
    }

    /**
     * @method renderCellContents
     * @param {object} cell
     * @param {integer} x
     * @param {integer} y
     * @return {*}
     */
    renderCellContents(cell, x, y) {
        const {protectingImage} = this.props;
        const {movers, eaten} = this.state;

        // Get the cell type.
        let type = cell.type;

        // If already eaten then swap to blank.
        if (eaten.find(e => e.x === x && e.y === y)) {
            type = 'blank';
        }

        // If a mover is at this position then render that.
        for (var i=0; i<movers.length; i++) {
            let mover = movers[i];

            if (x === mover.x && y === mover.y) {
                return (
                    <img
                        src={asset(`images/pacman/${mover.image}`)}
                        style={mover.type === 'me' ?
                            this.getStyleForCellContents(16, 24, 16, 4) :
                            this.getStyleForCellContents(24, 24, 12, 4)
                        }
                    />
                );
            }
        }

        // No mover at this position so render each of the default
        // icons that rest at this position.

        if (type === 'point') {
            return (
                <div
                    style={this.getStyleForCellContents(8, 8, 20, 12)} 
                    className="rounded-full bg-app-yellow"
                />
            );
        }

        if (type === 'antivirus') {
            return (
                <img
                    style={this.getStyleForCellContents(28, 28, 10, 2)} 
                    className="rounded-full"
                    src={asset('images/pacman/norton.png')}
                />
            );
        }

        if (type === 'end') {
            return (
                <img
                    style={this.getStyleForCellContents(32, 32, 8, 0)} 
                    className="rounded-full"
                    src={asset('images/pacman/protecting-images/' + protectingImage + '.png')}
                />
            );
        }

        if (type === 'blank') {
            return (
                <div
                    style={this.getStyleForCellContents(24, 24, 12, 4)} 
                    className="rounded-full bg-transparent"
                />
            );
        }
    }

    /**
     * @method getStyleForCell
     * @param {object} cell
     * @return {object}
     */
    getStyleForCell = (cell) => {
        const {scale} = this.state;

        // Set the border width (scale = 1 <=> 4px)
        let borderWidth = scale * 4;

        // Set the colour of the border.
        let color = '#009B9A';

        // Initiate an empty style object.
        let style = {};

        // Initiate each of the sides as an array.
        let sides = ['top', 'bottom', 'left', 'right'];

        // Loop through each side and set either a border or padding on that side.
        sides.forEach((side) => {
            let capitalSide = side.charAt(0).toUpperCase() + side.slice(1);

            if (cell[side]) {
                style['border' + capitalSide] = borderWidth + 'px solid ' + color;
            } else {
                style['padding' + capitalSide] = borderWidth + 'px';
            }
        });

        // Return the styles for the cell.
        return style;
    }

    /**
     * @method getStyleForCellContents
     * @param {integer} width
     * @param {integer} height
     * @param {integer} marginX
     * @param {integer} marginY
     * @return {object}
     */
    getStyleForCellContents = (width, height, marginX, marginY) => {
        const {scale} = this.state;

        // scale = 1 <=> width + (marginX * 2) = 48px
        // scale = 1 <=> height * (marginY * 2 = 32px

        return {
            width: (width * scale) + 'px',
            height: (height * scale) + 'px',
            margin: (marginY * scale) + 'px' + ' ' + (marginX * scale) + 'px'
        };
    }
}
